import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { LANGUAGE_CONSTANTS as LANG_CONST } from 'src/app/shared/constants/language-constants';
import { environment } from 'src/environments/environment';
import { TokenService } from 'src/app/shared/utils/token.service';
import { DrAuthService } from 'src/app/shared/utils/dr-auth-service';

@Injectable({
  providedIn: 'root',
})
export class CommonService {
  readonly CONSTANTS = LANG_CONST;
  ownProfileStatus: boolean = false;
  ownProfileSubject!: BehaviorSubject<boolean>;
  private userGroupIdSource = new BehaviorSubject<string>('');
  userGroupId$ = this.userGroupIdSource.asObservable();

  // disabled click event util data load of unit / accesspoint
  isLoadingUnitAndAccessPoint: boolean = false;
  isLoadingUnitAndAccessPointSubject!: BehaviorSubject<boolean>;

  isLoadingBuilding: boolean = false;
  isLoadingBuildingSubject!: BehaviorSubject<boolean>;

  getDashboardStatistics: string = '';
  getDashboardStatisticsSubject!: BehaviorSubject<string>;
  constructor(
    private tokenService: TokenService,
    private drAuth: DrAuthService
  ) {
    this.ownProfileSubject = new BehaviorSubject(this.ownProfileStatus);
    this.isLoadingUnitAndAccessPointSubject = new BehaviorSubject(
      this.isLoadingUnitAndAccessPoint
    );

    this.isLoadingBuildingSubject = new BehaviorSubject(this.isLoadingBuilding);

    this.getDashboardStatisticsSubject = new BehaviorSubject(
      this.getDashboardStatistics
    );
  }

  /**
   * @description generic error message dynamically replace {} with given string
   * @param message {string}
   * @param allowCount {string | [string]}
   * @returns string
   */
  stringInterPolation(message: string, allowCount: string | string[]): string {
    let updateMessage = '';
    if (typeof allowCount === 'string') {
      updateMessage = message.replace('{}', allowCount.toString());
    } else {
      let splitMessage = message.split('{}');
      for (let i = 0; i < allowCount?.length; i++) {
        splitMessage[i] += allowCount[i];
      }
      updateMessage = splitMessage.join('');
    }
    return updateMessage;
  }

  setOwnProfile(ownProfile: boolean) {
    this.ownProfileSubject.next(ownProfile);
  }

  setDashboardStatistics(siteId: string) {
    this.getDashboardStatisticsSubject.next(siteId);
  }

  /**
   * Sets the disabled click event util unit and access point load.
   * @param status - A boolean value indicating the loading status.
   *                true: Loading is diabled click event.
   *                false: Loading is clickable.
   */
  setLoadingUnitAndAccessPoint(status: boolean) {
    this.isLoadingUnitAndAccessPointSubject.next(status);
  }

  /**
   * Sets the disabled click event util building load.
   * @param status - A boolean value indicating the loading status.
   *                true: Loading is diabled click event.
   *                false: Loading is clickable.
   */
  setLoadingBuilding(status: boolean) {
    this.isLoadingBuildingSubject.next(status);
  }

  /**
   * Sets the user group ID for a given user.
   *
   * @param userGroupId {string} - The identifier of the user group to associate with the user.
   *
   * This method updates the user group ID for current selected Group notifying observers through
   * the `userGroupIdSource` subject. It is typically used in a reactive programming context,
   * allowing components or services to react to changes in the user group ID and update accordingly.
   */
  setUserGroupId(userGroupId: string) {
    this.userGroupIdSource.next(userGroupId);
  }
  normalizeString(str: string): string {
    return str.replace(/\s+/g, ' ').trim(); // replaces multiple and non-breaking spaces with a regular space
  }

  /**
   * Stores Cognito tokens for primary and secondary AWS regions in localStorage.
   *
   * This helper is used after multi-region Cognito sign-up or login,
   * saving both primary and secondary tokens in a structured format.
   *
   * LocalStorage Format:
   * - Primary:
   *   CognitoIdentityServiceProvider.{clientId}.{email}.accessToken
   *   CognitoIdentityServiceProvider.{clientId}.{email}.idToken
   *   CognitoIdentityServiceProvider.{clientId}.{email}.refreshToken
   *
   * - Secondary:
   *   CognitoIdentityServiceProvider.{clientIdSecondary}.{email}.secondary.accessToken
   *   CognitoIdentityServiceProvider.{clientIdSecondary}.{email}.secondary.idToken
   *   CognitoIdentityServiceProvider.{clientIdSecondary}.{email}.secondary.refreshToken
   *
   * @param regionTokens Array/Object of region token objects from API response
   * @param email User's email (used in localStorage keys)
   * @param primaryRegion Primary Cognito region name (e.g., 'us-east-1')
   * @param secondaryRegion Secondary Cognito region name (e.g., 'us-west-2')
   * @param clientId Cognito App Client ID for primary region
   * @param clientIdSecondary Cognito App Client ID for secondary region
   */
  storeRegionTokens(regionTokens: any, email: string, isSignUp = true): void {
    if (!email || !regionTokens) return;

    const { drRegion, drRegionSecondary } = environment;

    let primaryToken, secondaryToken;
    if (isSignUp) {
      primaryToken = regionTokens.find((r: any) => r.region === drRegion);
      secondaryToken = regionTokens.find(
        (r: any) => r.region === drRegionSecondary
      );
    } else {
      // For login flow, regionTokens is an object with region keys (usEast1, usWest2)
      // Try to get tokens from the configured regions first
      primaryToken = regionTokens[drRegion];
      secondaryToken = regionTokens[drRegionSecondary];

      // If primary token is not available or not successful, try secondary
      // This handles the case where one region might be unavailable
      if (!primaryToken || !primaryToken.success) {
        console.warn(
          `Primary region (${drRegion}) token not available, checking secondary`
        );
        if (secondaryToken && secondaryToken.success) {
          // Swap: use secondary as primary if primary is not available
          primaryToken = secondaryToken;
          secondaryToken = null;
        }
      }
    }
    this.tokenService.setTokens(primaryToken, secondaryToken, email);
    this.drAuth.init();
  }

  removeTokens(): void {
    this.tokenService.deleteTokens();
  }
}
